// src/lib/supabase.js - VERSIÓN SIMPLIFICADA
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'TU_SUPABASE_URL'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'TU_SUPABASE_ANON_KEY'

// Configuración básica sin persistencia
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: false, // Deshabilitado para simplificar
    persistSession: false,   // Sin persistencia
    detectSessionInUrl: false // Sin detección de URL
  }
})

// =========================================
// FUNCIONES DE AUTENTICACIÓN
// =========================================

export const signUp = async (email, password, username) => {
  try {
    console.log('Iniciando registro para:', email, username)
    
    // Verificar que el username no existe
    const { data: existingUser } = await supabase
      .from('profiles')
      .select('username')
      .eq('username', username)
      .maybeSingle()
    
    if (existingUser) {
      throw new Error('Este nombre de usuario ya está en uso')
    }

    // Registro
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username }
      }
    })

    if (authError) throw authError

    if (authData.session) {
      console.log('Usuario registrado con sesión activa')
      
      // Esperar a que el trigger cree el perfil
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Actualizar username
      try {
        await supabase.rpc('update_username_after_signup', {
          new_username: username
        })
      } catch (usernameError) {
        console.warn('No se pudo actualizar username:', usernameError)
      }
    }

    return { data: authData, error: null }
  } catch (error) {
    console.error('Error en registro:', error)
    return { data: null, error }
  }
}

export const signIn = async (identifier, password) => {
  try {
    console.log('Iniciando sesión para:', identifier)
    
    const isEmail = identifier.includes('@')
    let email = identifier
    
    if (!isEmail) {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('username', identifier)
        .maybeSingle()
      
      if (profileError || !profile) {
        throw new Error('Usuario no encontrado')
      }
      
      email = profile.email
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) throw error

    console.log('Sesión iniciada correctamente')
    return { data, error: null }
  } catch (error) {
    console.error('Error en signIn:', error)
    return { data: null, error }
  }
}

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    
    console.log('Sesión cerrada correctamente')
    return { error: null }
  } catch (error) {
    console.error('Error en signOut:', error)
    return { error }
  }
}

// =========================================
// FUNCIONES DE PERFIL
// =========================================

export const getCurrentUserProfile = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    
    if (userError) throw userError
    if (!user) throw new Error('No hay usuario autenticado')

    // Intentar usar RPC primero
    try {
      const { data: profileData, error: rpcError } = await supabase
        .rpc('get_user_profile_with_family', { user_id: user.id })

      if (!rpcError && profileData) {
        return { data: profileData, error: null }
      }
    } catch (rpcError) {
      console.warn('RPC falló, usando query manual:', rpcError)
    }

    // Fallback a query manual
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select(`
        id,
        username,
        email,
        initial_money,
        current_balance,
        family_id,
        created_at,
        updated_at
      `)
      .eq('id', user.id)
      .single()

    if (profileError) throw profileError

    // Obtener familia si existe
    let familyData = null
    if (profile.family_id) {
      const { data: family } = await supabase
        .from('families')
        .select('id, name, invite_code, created_by')
        .eq('id', profile.family_id)
        .single()

      familyData = family
    }

    const profileWithFamily = {
      ...profile,
      families: familyData
    }

    return { data: profileWithFamily, error: null }
  } catch (error) {
    console.error('Error obteniendo perfil:', error)
    return { data: null, error }
  }
}

export const updateInitialMoney = async (userId, amount) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ initial_money: amount })
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error actualizando dinero inicial:', error)
    return { data: null, error }
  }
}

// =========================================
// FUNCIONES DE CATEGORÍAS
// =========================================

export const getExpenseCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('expense_categories')
      .select('*')
      .order('name')

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error obteniendo categorías:', error)
    return { data: null, error }
  }
}

// =========================================
// FUNCIONES DE TRANSACCIONES
// =========================================

export const createTransaction = async (transactionData) => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .insert([transactionData])
      .select(`
        *,
        expense_categories (
          name,
          color,
          icon
        )
      `)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error creando transacción:', error)
    return { data: null, error }
  }
}

export const getUserTransactions = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select(`
        *,
        expense_categories (
          name,
          color,
          icon
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error obteniendo transacciones:', error)
    return { data: null, error }
  }
}

export const getFamilyTransactions = async (limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select(`
        *,
        profiles!inner (
          username,
          family_id
        ),
        expense_categories (
          name,
          color,
          icon
        )
      `)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error obteniendo transacciones familiares:', error)
    return { data: null, error }
  }
}

export const updateTransaction = async (transactionId, updateData) => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .update(updateData)
      .eq('id', transactionId)
      .select(`
        *,
        expense_categories (
          name,
          color,
          icon
        )
      `)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error actualizando transacción:', error)
    return { data: null, error }
  }
}

export const deleteTransaction = async (transactionId) => {
  try {
    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', transactionId)

    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error eliminando transacción:', error)
    return { error }
  }
}

// =========================================
// FUNCIONES DE FAMILIA
// =========================================

export const createFamily = async (familyName, userId) => {
  try {
    const { data, error } = await supabase
      .from('families')
      .insert([{
        name: familyName,
        created_by: userId
      }])
      .select()
      .single()

    if (error) throw error

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ family_id: data.id })
      .eq('id', userId)

    if (updateError) throw updateError
    return { data, error: null }
  } catch (error) {
    console.error('Error creando familia:', error)
    return { data: null, error }
  }
}

export const joinFamily = async (inviteCode, userId) => {
  try {
    const { data: family, error: familyError } = await supabase
      .from('families')
      .select('id, name')
      .eq('invite_code', inviteCode.toUpperCase())
      .single()

    if (familyError || !family) {
      throw new Error('Código de invitación inválido')
    }

    const { data, error } = await supabase
      .from('profiles')
      .update({ family_id: family.id })
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error
    return { data: { ...data, family }, error: null }
  } catch (error) {
    console.error('Error uniéndose a familia:', error)
    return { data: null, error }
  }
}

export const leaveFamily = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ family_id: null })
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('Error abandonando familia:', error)
    return { data: null, error }
  }
}

// =========================================
// FUNCIONES DE UTILIDAD
// =========================================

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount)
}

export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export default supabase